/**
 * QuickFilterChips — preset filter chips above the table.
 *
 * V1.0.x ships the four operator-actionable presets:
 *   - "All open" → status in (issued, partially_paid, overdue)
 *   - "Overdue 30d+" → status=overdue + age >= 30
 *   - "This week sent" → status=issued + dateFrom = monday-this-week
 *   - "Paid this month"
 *
 * Plus two visual stubs that mirror the prototype's IA but render
 * disabled with a tooltip naming the vertical that wires them:
 *   - "Awaiting reminder" — needs the receipts vertical to know which
 *     overdue invoices haven't been nudged this week.
 *   - "Save current view" — needs saved-views storage (V1.x).
 */
import { Clock, Filter as FilterIcon } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { formatLakhCrore } from "../lib/format-money.ts";

export type QuickFilter = null | "all-open" | "overdue-30" | "this-week-sent" | "paid-this-month";

export interface QuickFilterChipsProps {
  value: QuickFilter;
  onChange: (next: QuickFilter) => void;
  topExposure?: {
    customerName: string;
    totalPaise: string;
  } | null;
  onTopExposureFilter?: () => void;
}

interface Chip {
  value: NonNullable<QuickFilter>;
  label: string;
  icon?: LucideIcon;
}

const CHIPS: ReadonlyArray<Chip> = [
  { value: "all-open", label: "All open" },
  { value: "overdue-30", label: "Overdue 30d+", icon: Clock },
  { value: "this-week-sent", label: "This week sent" },
  { value: "paid-this-month", label: "Paid this month" },
];

export function QuickFilterChips({
  value,
  onChange,
  topExposure,
  onTopExposureFilter,
}: QuickFilterChipsProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1 text-sm text-muted">
        <FilterIcon size={12} aria-hidden />
        Quick filters
      </span>
      {CHIPS.map((c) => {
        const active = value === c.value;
        const Icon = c.icon;
        return (
          <button
            key={c.value}
            type="button"
            onClick={() => onChange(active ? null : c.value)}
            aria-pressed={active}
            className={[
              "inline-flex items-center gap-1.5 rounded-full border text-sm px-2.5 py-1 transition-colors",
              active
                ? "border-sky-500 bg-sky-50 text-sky-700"
                : "border-line bg-paper text-ink-2 hover:bg-bg hover:border-line-2",
            ].join(" ")}
          >
            {Icon ? <Icon size={12} aria-hidden /> : null}
            {c.label}
          </button>
        );
      })}
      {topExposure ? (
        <button
          type="button"
          onClick={onTopExposureFilter}
          className="inline-flex items-center gap-1.5 rounded-full border border-line bg-bg px-2.5 py-1 text-sm text-ink-2 transition-colors hover:bg-raised"
        >
          Owed by {topExposure.customerName} · {formatLakhCrore(topExposure.totalPaise)}
        </button>
      ) : null}
      {/* V1.x stubs ("Awaiting reminder", "Save current view") removed
       *  from V1.0 — disabled chips with hidden-tooltip explanations
       *  read as half-built. They'll come back as live chips when the
       *  receipts vertical and saved-views storage ship. */}
    </div>
  );
}
